import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Select, InputNumber } from 'antd';
import { MinusCircleOutlined, PlusOutlined, LeftCircleOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoading } from '../../context/LoadingContext';
import {apiGetListNetworkProvider, apiUpdateNetworkProvider} from '../../api';
import { toastError, toastSuccess } from '../../utils/toast';
import {cleanArray} from "../../utils/common-func";

interface UpdateNetworkProviderRequest {
  cpCode: string;
  name: string;
  subCpCode?: string;
  password?: string;
  pathCpPubKey?: string;
  pathDSPubKey?: string;
  pathDSPriKey?: string;
  apiUrl: string;
  weight?: number;
  secretKey?: string;
  devStaffCode?: string;
  source?: string;
  status?: string;
  proxyAddress?: string[];
  otpConfirm: string;
}

const sourceOptions = [
  { label: 'Đại trà', value: 'DataSponsor' },
  { label: 'Upgrade', value: 'Affapi' },
  { label: 'TKG', value: 'TKG' },
  { label: 'TV360', value: 'TV360Viettel' },
];

const statusOptions = [
  { label: 'Hoạt động', value: 'Active' },
  { label: 'Không hoạt động', value: 'Inactive' },
];

const INPUT_STYLE = {
  width: '90%',
};

const ICON_STYLE = {
  marginLeft: 8,
  fontSize: '16px',
  color: '#999'
};

export default function EditNetworkProvider() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsLoading } = useLoading();
  const [submitting, setSubmitting] = useState(false);
  const [providerCpCode, setProviderCpCode] = useState<string>('');

  const fetchProviderDetail = useCallback(async (cpCode: string) => {
    try {
      setIsLoading(true);
      const response = await apiGetListNetworkProvider();
      if (response.status === 200 && response.data.networks) {
        // Find the provider by cpCode from the list
        const provider = response.data.networks.find((p: any) => p.cpCode === cpCode);
        if (provider) {
          form.setFieldsValue({
            name: provider.name,
            cpCode: provider.cpCode,
            subCpCode: provider.subCpCode,
            password: provider.password,
            pathCpPubKey: provider.pathCpPubKey,
            pathDSPubKey: provider.pathDSPubKey,
            pathDSPriKey: provider.pathDSPriKey,
            apiUrl: provider.apiUrl,
            weight: provider.weight || 0,
            secretKey: provider.secretKey,
            devStaffCode: provider.devStaffCode,
            source: provider.source,
            status: provider.status || 'Active',
            proxyAddress: provider.proxyAddress && provider.proxyAddress.length > 0 ? provider.proxyAddress : [''],
          });
        } else {
          toastError('Không tìm thấy nhà cung cấp');
          navigate(-1);
        }
      }
    } catch (error: any) {
      console.error('Error fetching provider detail:', error);
      toastError('Không thể tải thông tin nhà cung cấp');
    } finally {
      setIsLoading(false);
    }
  }, [form, setIsLoading, navigate]);

  useEffect(() => {
    // Get provider cpCode from location state or query params
    const cpCode = location.state?.cpCode || new URLSearchParams(location.search).get('cpCode');
    if (cpCode) {
      setProviderCpCode(cpCode);
      fetchProviderDetail(cpCode);
    } else {
      toastError('Không tìm thấy mã nhà cung cấp');
      navigate(-1);
    }
  }, [location, fetchProviderDetail, navigate]);

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      setIsLoading(true);

      const updateData: UpdateNetworkProviderRequest = {
        ...values,
        cpCode: providerCpCode,
        proxyAddress: cleanArray(values.proxyAddress || []),
      };

      const resp = await apiUpdateNetworkProvider(updateData);
      if (resp.status === 200) {
        toastSuccess('Cập nhật dữ liệu thành công!');
        navigate(-1);
      }
    } catch (error: any) {
      console.error('Error updating network provider:', error);
      toastError('Vui lòng kiểm tra lại thông tin!');
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 200, marginBottom: 20 }}>
        <Button
          onClick={() => navigate(-1)}
          icon={<LeftCircleOutlined />}
          style={{ background: 'gray', color: 'white' }}
        >
          Trở lại
        </Button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Form
          style={{ width: '700px' }}
          form={form}
          onFinish={handleSubmit}
          labelAlign="left"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValues={{
            weight: 0,
            status: 'active',
          }}
        >
          <Form.Item
            label="Tên nhà cung cấp"
            name="name"
            rules={[{ required: true, message: 'Vui lòng nhập tên nhà cung cấp' }]}
          >
            <Input placeholder="Nhập tên nhà cung cấp" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Mã nhà cung cấp"
            name="cpCode"
            rules={[{ required: true, message: 'Vui lòng nhập mã nhà cung cấp' }]}
          >
            <Input placeholder="Nhập mã CP" style={INPUT_STYLE} disabled />
          </Form.Item>

          <Form.Item
            label="Mã nhà cung cấp phụ"
            name="subCpCode"
          >
            <Input placeholder="Nhập mã nhà cung cấp phụ (tùy chọn)" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="API URL"
            name="apiUrl"
            rules={[
              { required: true, message: 'Vui lòng nhập API URL' },
              { type: 'url', message: 'Vui lòng nhập URL hợp lệ' }
            ]}
          >
            <Input placeholder="https://example.com/api" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Nguồn"
            name="source"
            rules={[
              { required: true, message: 'Vui lòng chọn nguồn' },
            ]}
          >
            <Select
              placeholder="Chọn nguồn"
              options={sourceOptions}
              allowClear
              style={INPUT_STYLE}
            />
          </Form.Item>

          <Form.Item
            label="Trạng thái"
            name="status"
          >
            <Select
              placeholder="Chọn trạng thái"
              options={statusOptions}
              style={INPUT_STYLE}
            />
          </Form.Item>

          <Form.Item
            label="Weight"
            name="weight"
          >
            <InputNumber
              style={INPUT_STYLE}
              placeholder="Nhập weight"
              min={0}
            />
          </Form.Item>

          <Form.Item
            label="Mã nhân viên"
            name="devStaffCode"
          >
            <Input placeholder="Nhập mã nhân viên" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Mật khẩu"
            name="password"
          >
            <Input.Password placeholder="Nhập mật khẩu" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Secret Key"
            name="secretKey"
          >
            <Input.Password placeholder="Nhập secret key" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="PathCpPubKey"
            name="pathCpPubKey"
          >
            <Input placeholder="Nhập PathCpPubKey" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="PathDSPubKey"
            name="pathDSPubKey"
          >
            <Input placeholder="Nhập PathDSPubKey" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="PathDSPriKey"
            name="pathDSPriKey"
          >
            <Input placeholder="Nhập PathDSPriKey" style={INPUT_STYLE} />
          </Form.Item>

          <Form.List name="proxyAddress" initialValue={['']}>
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {fields.map((field, index) => (
                  <Form.Item
                    label={index === 0 ? "Địa chỉ Proxy" : " "}
                    colon={index === 0}
                    key={field.key}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        noStyle
                      >
                        <Input
                          placeholder="Nhập địa chỉ proxy"
                          style={INPUT_STYLE}
                        />
                      </Form.Item>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {fields.length > 1 && (
                          <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={() => remove(field.name)}
                            style={ICON_STYLE}
                          />
                        )}
                        {index === fields.length - 1 && (
                          <PlusOutlined
                            onClick={() => add()}
                            style={ICON_STYLE}
                          />
                        )}
                      </div>
                    </div>
                  </Form.Item>
                ))}
              </div>
            )}
          </Form.List>

          <Form.Item
            label="Mã OTP xác nhận"
            name="otpConfirm"
            rules={[{ required: true, message: 'Vui lòng nhập mã OTP' }]}
          >
            <Input placeholder="Nhập OTP xác nhận" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Cập nhật
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
}

